import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Inbox, Send, ArrowLeftRight, Package, FileText, BarChart3, Loader2, Bell, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import * as authApi from '@/services/authApi';
import { filterOperations } from '@/utils/operationAccess';

/**
 * SectionDashboard - Enhanced dashboard component for warehouse sections with custom operations
 * 
 * Standard tiles (6):
 * 1. Inward Consignment
 * 2. Shipment Request
 * 3. Internal Transfers
 * 4. Bundle Receipt Entry
 * 5. Day Wise Transaction Report
 * 6. Closing Stock Report
 * 
 * Props:
 * - sectionName: Display name (e.g., "FOB Section")
 * - sectionId: URL-safe ID (e.g., "fob")
 * - sectionKey: Database key (e.g., "FOB")
 * - description: Section description
 * - backRoute: Route to go back to (default: "/dashboard")
 * - operations: Array of section-specific operation tiles
 * - utilities: Array of utility tiles (Staff Performance, USN Reprint, etc.)
 * - adminTiles: Array of admin-only tiles (Location Setup, etc.)
 * - statsComponent: Optional React component for stats display
 * - alertComponent: Optional React component for alerts (e.g., StagingAlert)
 * - headerIcon: Optional icon component for header
 * - customCommonTiles: Override default common tiles if needed
 * - showBundleReceipt: Whether to show Bundle Receipt in common tiles (default: false)
 */

const SectionDashboard = ({ 
  sectionName, 
  sectionId, 
  sectionKey,
  moduleId = null,
  description = "Operations Dashboard",
  backRoute = "/dashboard",
  operations = [],
  utilities = [],
  adminTiles = [],
  statsComponent = null,
  alertComponent = null,
  headerIcon: HeaderIcon = null,
  customCommonTiles = null,
  showBundleReceipt = false,
  notificationBadge = 0
}) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await authApi.getMe();
        setUser(userData);
      } catch (error) {
        console.error('Error fetching user:', error);
        navigate('/login');
      } finally {
        setIsLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  const basePath = `/sections/${sectionId}`;
  
  // Default common tiles (5 standard management tiles)
  const defaultCommonTiles = [
    {
      id: 'inward-consignment',
      title: 'Inward Consignment',
      description: 'Receive and process incoming consignments',
      icon: Inbox,
      route: `${basePath}/inward-consignment`,
      color: 'bg-emerald-500'
    },
    {
      id: 'shipment-request',
      title: 'Shipment Request',
      description: 'View and process incoming shipment requests',
      icon: Send,
      route: `${basePath}/shipment-requests`,
      color: 'bg-orange-500'
    },
    {
      id: 'internal-transfers',
      title: 'Internal Transfers',
      description: 'Handle transfers within/between sections',
      icon: ArrowLeftRight,
      route: `${basePath}/internal-transfer`,
      color: 'bg-indigo-500'
    },
    {
      id: 'daily-report',
      title: 'Day Wise Transaction Report',
      description: 'Daily summary report',
      icon: FileText,
      route: `${basePath}/report`,
      color: 'bg-slate-600'
    },
    {
      id: 'closing-stock',
      title: 'Closing Stock Report',
      description: 'Live closing stock by operation',
      icon: BarChart3,
      route: `${basePath}/closing-stock`,
      color: 'bg-emerald-600'
    },
    {
      id: 'misc-outward',
      title: 'Misc Outward',
      description: 'Send goods outward for sample/misc purposes',
      icon: ExternalLink,
      route: `${basePath}/misc-outward`,
      color: 'bg-rose-500'
    },
    {
      id: 'inventory-report',
      title: 'Inventory Report',
      description: 'View current section inventory with filters & export',
      icon: BarChart3,
      route: `${basePath}/inventory-report`,
      color: 'bg-indigo-600'
    }
  ];

  const commonTiles = customCommonTiles || defaultCommonTiles;

  // Filter tiles based on user's allowed_operations for this module.
  // Permission key = moduleId when provided (it must match build_master_modules
  // module_id, e.g. 'greige_storage'), else the URL-safe sectionId.
  const permModuleId = moduleId || sectionId;
  const filteredCommonTiles = filterOperations(permModuleId, commonTiles);
  const filteredOperations = filterOperations(permModuleId, operations);
  const filteredUtilities = filterOperations(permModuleId, utilities);
  const filteredAdminTiles = filterOperations(permModuleId, adminTiles);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2D5F4F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate(backRoute)} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              {HeaderIcon && <HeaderIcon className="w-6 h-6 text-[#E8DCC8]" />}
              <div>
                <h1 className="text-xl font-bold font-display">{sectionName}</h1>
                <p className="text-sm text-[#E8DCC8]">{description}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {notificationBadge > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/10 relative"
                onClick={() => navigate('/notifications')}
              >
                <Bell className="h-5 w-5" />
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                  {notificationBadge > 9 ? '9+' : notificationBadge}
                </span>
              </Button>
            )}
            {user && (
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium">{user.name}</p>
                <p className="text-xs text-[#E8DCC8]">{user.role}</p>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Alert Component (e.g., StagingAlert) */}
        {alertComponent && <div className="mb-6">{alertComponent}</div>}

        {/* Stats Component */}
        {statsComponent && <div className="mb-8">{statsComponent}</div>}

        {/* Section Management Tiles */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[#1A1F2E] mb-2 font-display">Section Management</h2>
          <p className="text-[#5A5F6E]">Consignments, Transfers & Reports</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-12">
          {filteredCommonTiles.map((tile) => {
            const Icon = tile.icon;
            return (
              <Card
                key={tile.id}
                className="bg-white hover:shadow-lg transition-all duration-200 cursor-pointer border-0 shadow-md hover:border-[#2D5F4F] border-2 border-transparent"
                onClick={() => navigate(tile.route)}
                data-testid={`common-tile-${tile.id}`}
              >
                <CardContent className="p-4 flex flex-col items-center text-center">
                  <div className={`w-12 h-12 ${tile.color} rounded-lg flex items-center justify-center mb-3 relative`}>
                    <Icon className="w-6 h-6 text-white" />
                    {tile.badge > 0 && (
                      <Badge className="absolute -top-2 -right-2 bg-red-500 text-white text-xs">
                        {tile.badge}
                      </Badge>
                    )}
                  </div>
                  <h3 className="text-sm font-semibold text-[#1A1F2E] mb-1 font-display">{tile.title}</h3>
                  <p className="text-xs text-[#5A5F6E]">{tile.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Operations Section */}
        {filteredOperations.length > 0 && (
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-2 font-display">Operations</h2>
              <p className="text-[#5A5F6E]">Choose an operation to perform</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {filteredOperations.map((operation, index) => {
                const Icon = operation.icon;
                return (
                  <div
                    key={operation.id}
                    onClick={() => !operation.disabled && navigate(operation.route)}
                    className={`bg-white rounded-lg border-2 shadow-sm transition-all duration-200 p-5 ${
                      operation.disabled 
                        ? 'border-gray-200 opacity-60 cursor-not-allowed' 
                        : 'border-[#E5DFD0] hover:border-[#2D5F4F] hover:shadow-md cursor-pointer'
                    }`}
                    data-testid={`operation-tile-${operation.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-lg ${operation.color} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-[#5A5F6E] bg-gray-100 px-2 py-0.5 rounded">
                            {index + 1}
                          </span>
                          {operation.disabled && (
                            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Coming Soon</span>
                          )}
                        </div>
                        <h3 className="text-base font-semibold text-[#1A1F2E] mb-1 font-display truncate">
                          {operation.title}
                        </h3>
                        <p className="text-sm text-[#5A5F6E] line-clamp-2">{operation.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Utilities Section */}
        {filteredUtilities.length > 0 && (
          <>
            <div className="mb-8">
              <h2 className="text-xl font-bold text-[#1A1F2E] mb-4 font-display">Utilities</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {filteredUtilities.map((utility) => {
                const Icon = utility.icon;
                return (
                  <div
                    key={utility.id}
                    onClick={() => navigate(utility.route)}
                    className="bg-white rounded-lg border-2 border-[#E5DFD0] hover:border-[#2D5F4F] hover:shadow-md cursor-pointer transition-all duration-200 p-6"
                    data-testid={utility.testId || `utility-tile-${utility.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-14 h-14 rounded-lg ${utility.color} flex items-center justify-center`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-[#1A1F2E] mb-1 font-display">
                          {utility.title}
                        </h3>
                        <p className="text-sm text-[#5A5F6E]">{utility.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Admin Section */}
        {filteredAdminTiles.length > 0 && (
          <>
            <div className="mb-8">
              <h2 className="text-xl font-bold text-[#1A1F2E] mb-4 font-display">Admin Setup</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAdminTiles.map((tile) => {
                const Icon = tile.icon;
                return (
                  <div
                    key={tile.id}
                    onClick={() => navigate(tile.route)}
                    className="bg-white rounded-lg border-2 border-dashed border-[#E5DFD0] shadow-sm hover:border-[#2D5F4F] hover:shadow-md cursor-pointer transition-all duration-200 p-5"
                    data-testid={`admin-tile-${tile.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-lg ${tile.color} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-[#1A1F2E] mb-1 font-display">
                          {tile.title}
                        </h3>
                        <p className="text-sm text-[#5A5F6E]">{tile.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default SectionDashboard;
